import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export interface BackgroundTask {
  /** Unique task identifier */
  id: string
  /** Short label, e.g. "Firmensuche", "Kontakte finden" */
  label: string
  /** Current status detail, e.g. "Steuerberatung in Moenchengladbach..." */
  detail: string
  /** Task state */
  status: 'running' | 'done' | 'error'
  /** Structured log entries (optional) */
  log?: Array<{ step: string; detail: string; status: string }>
  /** Timestamp when last updated */
  updatedAt: number
}

interface BackgroundTasksState {
  tasks: Record<string, BackgroundTask>
  /** Whether task recovery has run after rehydration */
  _recovered: boolean
  /** Add or update a task */
  upsertTask: (task: BackgroundTask) => void
  /** Remove a task by id */
  removeTask: (id: string) => void
  /** Mark recovery as done */
  markRecovered: () => void
  /** Get all active (running) tasks */
  getRunningTasks: () => BackgroundTask[]
  /** Get all tasks (including recently completed) */
  getAllTasks: () => BackgroundTask[]
}

export const useBackgroundTasksStore = create<BackgroundTasksState>()(
  persist(
    (set, get) => ({
      tasks: {},
      _recovered: false,

      upsertTask: (task) =>
        set((state) => ({
          tasks: { ...state.tasks, [task.id]: { ...task, updatedAt: Date.now() } },
        })),

      removeTask: (id) =>
        set((state) => {
          const { [id]: _, ...rest } = state.tasks
          return { tasks: rest }
        }),

      markRecovered: () => set({ _recovered: true }),

      getRunningTasks: () =>
        Object.values(get().tasks).filter((t) => t.status === 'running'),

      getAllTasks: () =>
        Object.values(get().tasks).sort((a, b) => b.updatedAt - a.updatedAt),
    }),
    {
      name: 'background-tasks',
      partialize: (state) => ({
        tasks: Object.fromEntries(
          Object.entries(state.tasks).filter(([, t]) => t.status === 'running')
        ),
      }),
    }
  )
)
